import React, { useCallback, useState } from 'react';
import { useWindows } from '../../contexts/WindowContext';
import { useSound } from '../../contexts/SoundContext';
import { Icon } from '../Icons';
import { START_MENU_LEFT_ITEMS, START_MENU_RIGHT_ITEMS, ABOUT_INFO, EXTERNAL_LINK_APPS } from '../../utils/constants';
import type { AppType, StartMenuItem } from '../../types';
import styles from './StartMenu.module.css';

// All Programs menu structure
const ALL_PROGRAMS_MENU: StartMenuItem[] = [
  {
    id: 'accessories',
    name: 'Accessories',
    icon: 'folder',
    children: [
      { id: 'notepad', name: 'Notepad', icon: 'notepad', appType: 'notepad' },
      { id: 'command-prompt', name: 'Command Prompt', icon: 'command-prompt', appType: 'command-prompt' },
    ],
  },
  {
    id: 'games',
    name: 'Games',
    icon: 'folder',
    children: [
      { id: 'minecraft', name: 'Minecraft', icon: 'minecraft', appType: 'minecraft' },
      { id: 'minesweeper', name: 'Minesweeper', icon: 'minesweeper', appType: 'minesweeper' },
      { id: 'solitaire', name: 'Solitaire', icon: 'solitaire', appType: 'solitaire' },
    ],
  },
  {
    id: 'startup',
    name: 'Startup',
    icon: 'folder',
    children: [],
  },
  { id: 'divider-1', name: '', icon: '' },
  { id: 'ie', name: 'Internet Explorer', icon: 'internet-explorer', appType: 'internet-explorer' },
  { id: 'msn', name: 'MSN Messenger', icon: 'msn-messenger', appType: 'msn-messenger' },
];

interface StartMenuProps {
  onClose: () => void;
}

export const StartMenu: React.FC<StartMenuProps> = ({ onClose }) => {
  const { openWindow } = useWindows();
  const { playSound } = useSound();
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  const handleItemClick = useCallback((appType: AppType) => {
    // Check if this is an external link app
    const externalUrl = EXTERNAL_LINK_APPS[appType];
    if (externalUrl) {
      playSound('menuCommand');
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      onClose();
      return;
    }

    openWindow(appType);
    playSound('menuCommand');
    onClose();
  }, [openWindow, playSound, onClose]);

  const handleAllProgramsEnter = useCallback(() => {
    setShowAllPrograms(true);
    playSound('menuPopup');
  }, [playSound]);

  const handleAllProgramsLeave = useCallback(() => {
    // Small delay before hiding to allow moving to submenu
    setTimeout(() => {
      setShowAllPrograms(false);
      setActiveSubmenu(null);
    }, 100);
  }, []);

  const handleSubmenuEnter = useCallback((id: string) => {
    setActiveSubmenu(id);
    playSound('menuPopup');
  }, [playSound]);

  const handleLogOff = useCallback(() => {
    // Reload the page as a fun "log off"
    playSound('shutdown');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [playSound]);

  const handleShutDown = useCallback(() => {
    // Fun shutdown - fade to black then reload
    playSound('shutdown');
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #000;
      z-index: 99999;
      opacity: 0;
      transition: opacity 1s;
    `;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  }, [playSound]);

  return (
    <div
      className={styles.startMenu}
      data-start-menu
      role="menu"
      aria-label="Start menu"
    >
      {/* Header with user */}
      <div className={styles.header}>
        <div className={styles.avatar}>
          <Icon name="user" size={48} />
        </div>
        <span className={styles.userName}>{ABOUT_INFO.name}</span>
      </div>

      {/* Main content area */}
      <div className={styles.content}>
        {/* Left column - Pinned programs */}
        <div className={styles.leftColumn}>
          <div className={styles.section}>
            {START_MENU_LEFT_ITEMS.map((item) => (
              <button
                key={item.id}
                className={styles.menuItem}
                onClick={() => item.appType && handleItemClick(item.appType)}
                role="menuitem"
              >
                <Icon name={item.icon} size={32} className={styles.itemIcon} />
                <span className={styles.itemText}>{item.name}</span>
              </button>
            ))}
          </div>

          <div className={styles.separator} />

          <div
            className={styles.allPrograms}
            onMouseEnter={handleAllProgramsEnter}
            onMouseLeave={handleAllProgramsLeave}
          >
            <button
              className={`${styles.allProgramsButton} ${showAllPrograms ? styles.active : ''}`}
              role="menuitem"
              onClick={() => setShowAllPrograms(prev => !prev)}
            >
              All Programs
              <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
                <path d="M2 0 L6 4 L2 8 Z"/>
              </svg>
            </button>

            {/* All Programs Flyout */}
            {showAllPrograms && (
              <div className={styles.allProgramsFlyout}>
                {ALL_PROGRAMS_MENU.map((item) => {
                  // Divider
                  if (!item.name) {
                    return <div key={item.id} className={styles.flyoutDivider} />;
                  }

                  // Folder with children
                  if (item.children) {
                    return (
                      <div
                        key={item.id}
                        className={styles.flyoutItemContainer}
                        onMouseEnter={() => handleSubmenuEnter(item.id)}
                        onMouseLeave={() => setActiveSubmenu(null)}
                      >
                        <button
                          className={`${styles.flyoutItem} ${activeSubmenu === item.id ? styles.active : ''}`}
                          role="menuitem"
                          onClick={() => setActiveSubmenu(prev => prev === item.id ? null : item.id)}
                        >
                          <Icon name={item.icon} size={16} className={styles.flyoutIcon} />
                          <span className={styles.flyoutText}>{item.name}</span>
                          {item.children.length > 0 && (
                            <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor" className={styles.flyoutArrow}>
                              <path d="M1 0 L5 3 L1 6 Z"/>
                            </svg>
                          )}
                        </button>

                        {/* Submenu */}
                        {activeSubmenu === item.id && item.children.length > 0 && (
                          <div className={styles.submenu}>
                            {item.children.map((child) => (
                              <button
                                key={child.id}
                                className={styles.flyoutItem}
                                onClick={() => child.appType && handleItemClick(child.appType)}
                                role="menuitem"
                              >
                                <Icon name={child.icon} size={16} className={styles.flyoutIcon} />
                                <span className={styles.flyoutText}>{child.name}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Empty folder message */}
                        {activeSubmenu === item.id && item.children.length === 0 && (
                          <div className={styles.submenu}>
                            <span className={styles.emptyFolder}>(Empty)</span>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Regular item
                  return (
                    <button
                      key={item.id}
                      className={styles.flyoutItem}
                      onClick={() => item.appType && handleItemClick(item.appType)}
                      role="menuitem"
                    >
                      <Icon name={item.icon} size={16} className={styles.flyoutIcon} />
                      <span className={styles.flyoutText}>{item.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column - System items */}
        <div className={styles.rightColumn}>
          {START_MENU_RIGHT_ITEMS.map((item) => (
            <button
              key={item.id}
              className={styles.menuItem}
              onClick={() => item.appType && handleItemClick(item.appType)}
              role="menuitem"
            >
              <Icon name={item.icon} size={24} className={styles.itemIcon} />
              <span className={styles.itemText}>{item.name}</span>
            </button>
          ))}

          <div className={styles.separator} />

          {/* Additional system items */}
          <button
            className={styles.menuItem}
            onClick={() => handleItemClick('recycle-bin')}
            role="menuitem"
          >
            <Icon name="recycle-bin" size={24} className={styles.itemIcon} />
            <span className={styles.itemText}>Recycle Bin</span>
          </button>
        </div>
      </div>

      {/* Footer with buttons */}
      <div className={styles.footer}>
        <button className={styles.footerButton} onClick={handleLogOff}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1 L8 8 M4 4 A6 6 0 1 0 12 4" stroke="currentColor" fill="none" strokeWidth="2"/>
          </svg>
          Log Off
        </button>
        <button className={styles.footerButton} onClick={handleShutDown}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 3 L8 8" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Shut Down
        </button>
      </div>
    </div>
  );
};

export default StartMenu;
